import clsx from "clsx";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import locale from "../../../../constants/locale";
import { UserType } from "../../../../models";
import { useStore } from "../../../../stores";
import { Popover } from "react-tiny-popover";
import { safeParse } from "../../../../utils/arrays";
import UpdateAndDisconnectModal from "./UpdateAndDisconnectModal";

export default function DisconnectButton({
  ongoingCall,
}: {
  ongoingCall: boolean;
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { users } = useStore("users");
  const {
    selectedChat,
    loadMessages,
    disconnect,
    updateGuestUsers,
    setSelectedChatDisconnect,
  } = useStore("chats");
  const [openDealUpdate, setOpenDealUpdate] = useState(false);
  const { logEvent } = useStore("audit");
  const { user, getPermissions } = useStore("auth");
  const { selectedBot } = useStore("bots");
  const { findDealByContact } = useStore("boards");
  const [isLoading, setIsLoading] = useState(false);
  const skipClosingDialog = useRef(false);
  const { t } = useTranslation();

  const guestUsers = useMemo(
    () => safeParse(selectedChat?.data?.guest_users) ?? [],
    [selectedChat?.data?.guest_users]
  );
  const isGuestUser = guestUsers?.includes(user?.id);

  const dealExists = useCallback(async () => {
    if (!selectedChat?.id) return false;
    try {
      await findDealByContact(selectedChat?.id);
      return true;
    } catch (error) {
      return false;
    }
  }, [selectedChat?.id, findDealByContact]);

  const handleClick = useCallback(
    async (skipDealUpdateCheck = false) => {
      if (!selectedChat) {
        return;
      }
      setIsLoading(true);

      const conversationStatusEnabled =
        selectedBot?.settings?.conversationStatusConfig?.enabled;
      const shouldShowModal =
        !skipDealUpdateCheck &&
        (conversationStatusEnabled ||
          (selectedBot?.settings?.dealUpdateRequired &&
            (await dealExists())));

      if (shouldShowModal) {
        setIsLoading(false);
        setOpenDealUpdate(true);
        return;
      }

      if (isGuestUser) {
        await updateGuestUsers(
          guestUsers?.filter((f: string) => f !== user?.id)
        );
        setSelectedChatDisconnect(false);
      } else {
        console.log("skipClosingDialog", skipClosingDialog?.current);
        await disconnect(0, skipClosingDialog?.current);
        await updateGuestUsers([]);
        let eventName = "agent_disconnected";

        logEvent(eventName, {
          id: user?.id,
          email: user?.email,
          botId: selectedBot?.id,
          chatId: selectedChat.id,
        });
      }
      loadMessages(200);
      setIsLoading(false);
    },
    [
      selectedChat,
      skipClosingDialog,
      selectedBot?.settings?.dealUpdateRequired,
      selectedBot?.settings?.conversationStatusConfig?.enabled,
      selectedBot?.id,
      isGuestUser,
      loadMessages,
      updateGuestUsers,
      guestUsers,
      setSelectedChatDisconnect,
      user?.id,
      user?.email,
      disconnect,
      logEvent,
      dealExists,
    ]
  );

  const otherAgentConnected =
    user?.type === UserType.Collaborator &&
    !!selectedChat?.userConnected &&
    selectedChat?.userConnected !== user?.id &&
    !isGuestUser;

  const agentConnected = useMemo(
    () => users?.find((f) => f.id === selectedChat?.userConnected),
    [selectedChat?.userConnected, users]
  );

  const disabled = ongoingCall || otherAgentConnected;

  let text = locale.disconnect;

  if (otherAgentConnected) {
    text = agentConnected
      ? locale.agentConnected
      : locale.anAgentIsAlreadyConnected;
  }

  if (!isGuestUser && getPermissions()?.canSkipClosingDialog) {
    return (
      <>
        <Popover
          isOpen={popoverOpen}
          positions={["top", "right"]}
          align={"start"}
          containerStyle={{ zIndex: "10000", width: "300px" }}
          padding={7}
          onClickOutside={() => setPopoverOpen(false)}
          content={() => (
            <div className="has-border has-background-white has-rounded-corners">
              <div
                onClick={() => {
                  if (!isLoading) {
                    skipClosingDialog.current = false;
                    handleClick();
                    setPopoverOpen(false);
                  }
                }}
                className="is-clickable has-border-top py-2 px-5"
              >
                <span className="is-6 mb-0 subtitle">
                  {t(locale.disconnectNow)}
                </span>
              </div>
              <div
                onClick={() => {
                  if (!isLoading) {
                    skipClosingDialog.current = true;
                    handleClick();
                    setPopoverOpen(false);
                  }
                }}
                className="is-clickable has-border-top py-2 px-5"
              >
                <span className="is-6 mb-0 subtitle">
                  {t(locale.disconnectAndSkipClosingDialog)}
                </span>
              </div>
            </div>
          )}
        >
          <button
            onClick={() => setPopoverOpen(true)}
            className={clsx(
              "button is-ghost is-no-box-shadow has-text-primary",
              {
                "is-loading": isLoading,
              }
            )}
            disabled={disabled}
            title={t(text, { name: agentConnected?.name })}
          >
            {!isLoading && (
              <span className="icon">
                <img src="/assets/disconnect-chat.svg" alt="" />
              </span>
            )}
          </button>
        </Popover>
        {openDealUpdate && (
          <UpdateAndDisconnectModal
            open
            onClose={() => setOpenDealUpdate(false)}
            onUpdate={() => {
              setOpenDealUpdate(false);
              handleClick(true);
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <button
        onClick={() => handleClick()}
        className={clsx("button is-ghost is-no-box-shadow has-text-primary", {
          "is-loading": isLoading,
        })}
        disabled={isLoading || disabled}
        title={t(text, { name: agentConnected?.name })}
      >
        {!isLoading && (
          <span className="icon">
            <img src="/assets/disconnect-chat.svg" alt="" />
          </span>
        )}
      </button>
      {openDealUpdate && (
        <UpdateAndDisconnectModal
          open
          onClose={() => setOpenDealUpdate(false)}
          onUpdate={() => {
            setOpenDealUpdate(false);
            handleClick(true);
          }}
        />
      )}
    </>
  );
}
